import { AnalysisRow } from './types';
import { normalizeForMatch } from './textUtils';

// Generic section-title words that should not be mistaken for an API name.
const GENERIC_HEADING = /^(summary|analysis|recommendation|overview|result|coverage|response|assessment|details|conclusion|background|context|notes?)s?$/i;

// True when `v` looks like a real API/service name (not a section title, HTTP
// method or endpoint path).
const looksLikeApiName = (v: string): boolean =>
  !!v && !GENERIC_HEADING.test(v) && !/^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/i.test(v) && !v.startsWith('/');

// Pulls the API/service named in a "Recommendation" section. Used when a checked
// API does NOT cover the capability and the reply points to a different one — that
// recommended API is what should land in the table.
const extractRecommendedApi = (text: string): string => {
  if (!text) return "";
  const clean = (s: string) => s.replace(/[#*_`]/g, '').trim();
  const lines = text.split('\n');
  const isRecHeading = (l: string) => /^\s*(?:#{1,4}\s+)?\*{0,2}recommendation[s]?\*{0,2}\s*:?\s*$/i.test(l.trim());

  const start = lines.findIndex(isRecHeading);
  if (start === -1) return "";

  const body: string[] = [];
  for (let j = start + 1; j < lines.length; j++) {
    if (/^\s*#{1,4}\s+/.test(lines[j])) break; // next heading ends the section
    body.push(lines[j]);
  }
  const block = body.join('\n');

  // 1) First non-generic bold phrase in the recommendation body.
  const bold = [...block.matchAll(/\*\*(.+?)\*\*/g)].map(b => clean(b[1])).find(looksLikeApiName);
  if (bold) return bold;

  // 2) "look into / use / consider (the) <X> service|api|endpoint".
  const plain = block.replace(/[*_`]/g, '');
  const phrase = plain.match(/\b(?:look into|use|consider|try|refer to|see|explore)\s+(?:the\s+)?(.+?)\s+(?:service|api|endpoint)s?\b/i);
  if (phrase) {
    const val = clean(phrase[1]);
    if (looksLikeApiName(val)) return val;
  }
  return "";
};

// Pulls the matched/recommended API name out of a recheck reply.
// The reply reliably leads with the matched API as its first section heading, so
// that is the primary source; an explicit "API: <name>" label, first bold phrase
// and first line act as fallbacks. Prefers text BEFORE the COVERAGE marker.
export const extractApiName = (text: string): string => {
  const clean = (s: string) => s.replace(/[#*_`]/g, '').replace(/\r/g, '').trim();

  const scan = (chunk: string): string => {
    if (!chunk.trim()) return "";

    // 1) First non-generic heading — the reply leads with the matched API name.
    const heading = [...chunk.matchAll(/^#{1,4}\s+(.+?)\s*$/gm)]
      .map(h => clean(h[1]))
      .find(h => h && !GENERIC_HEADING.test(h));
    if (heading) return heading;

    // 2) Explicit "API: <name>" / "Recommended API / Endpoint: <name>" label.
    const apiLabel = chunk.match(
      /^[ \t>]*[-*]?\s*\*{0,2}(?:recommended\s+|best[-\s]?fit\s+|matched\s+|suggested\s+)?api(?:\s*\/\s*endpoint)?\*{0,2}\s*[:\-–]\s*(.+)$/im,
    );
    if (apiLabel) {
      const val = clean(apiLabel[1]);
      if (val) return val;
    }

    // 3) First bold phrase that isn't a generic section title.
    const bolds = [...chunk.matchAll(/\*\*(.+?)\*\*/g)].map(b => clean(b[1]));
    const bold = bolds.find(b => b && !GENERIC_HEADING.test(b));
    if (bold) return bold;

    // 4) First meaningful line.
    const firstLine = chunk
      .split('\n')
      .map(l => clean(l))
      .find(l => l.length > 0 && !GENERIC_HEADING.test(l));
    return firstLine ?? "";
  };

  const covPos = text.search(/COVERAGE:\s*(gap|partial|covered)/i);
  const before = covPos !== -1 ? text.slice(0, covPos) : text;
  // Text after the marker with the marker line itself stripped out.
  const after = covPos !== -1
    ? text.slice(covPos).replace(/^COVERAGE:\s*(gap|partial|covered)\b.*$/im, '')
    : "";

  // A recheck reply may lead with the CHECKED API (heading) yet recommend a
  // different one; the recommended API is what the table should show, so prefer it.
  const recommended = extractRecommendedApi(before) || extractRecommendedApi(after);
  if (recommended) return recommended;

  return scan(before) || scan(after);
};

// Patches the row for `capability` in an epic analysis markdown table, setting its
// coverage cell to the rechecked value and its best-fit cell to the matched API.
export const updateAnalysisTable = (
  analysisMd: string,
  capability: string,
  coverage: string,
  apiName: string,
): string => {
  const capNorm = normalizeForMatch(capability);
  const label = coverage
    ? coverage.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
    : '';
  const safeApi = apiName ? apiName.replace(/\|/g, '\\|').replace(/\s+/g, ' ').trim() : '';

  return analysisMd.split('\n').map(line => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|')) return line;

    const cells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|');
    if (cells.length < 2) return line;
    if (cells.every(c => /^\s*:?-{3,}:?\s*$/.test(c))) return line; // separator row
    if (normalizeForMatch(cells[0]) !== capNorm) return line; // not the target row

    let covIdx = -1;
    cells.forEach((c, i) => {
      const v = c.trim().toLowerCase();
      if (v === 'gap' || v === 'partial' || v === 'covered' || v === 'under review') covIdx = i;
    });

    const newCells = [...cells];
    if (covIdx !== -1 && label) newCells[covIdx] = ` ${label} `;
    // The "Recommended API / Endpoint" column is the one immediately before
    // Coverage; write the matched API there (not into Basepath).
    const apiIdx = covIdx > 1 ? covIdx - 1 : -1;
    if (safeApi && apiIdx !== -1) newCells[apiIdx] = ` ${safeApi} `;
    // Basepath column(s) between Capability and the API column → '-'.
    for (let i = 1; i < apiIdx; i++) {
      if (i !== covIdx) newCells[i] = ' - ';
    }
    // Agent's Analysis (second-to-last) and Open Queries (last) → '-'.
    const lastIdx = newCells.length - 1;
    const analysisIdx = lastIdx - 1;
    if (analysisIdx >= 1 && analysisIdx !== covIdx && analysisIdx !== apiIdx) {
      newCells[analysisIdx] = ' - ';
    }
    if (lastIdx >= 1 && lastIdx !== covIdx && lastIdx !== apiIdx) {
      newCells[lastIdx] = ' - ';
    }
    return `|${newCells.join('|')}|`;
  }).join('\n');
};

// Reads capability + best-fit API (with HTTP method / endpoint path when present)
// out of an epic analysis markdown table.
export const parseAnalysisRows = (analysisMd: string): AnalysisRow[] => {
  const rows: AnalysisRow[] = [];
  analysisMd.split('\n').forEach(line => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('|')) return;
    const cells = trimmed.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
    if (cells.length < 2) return;
    if (cells.every(c => /^:?-{3,}:?$/.test(c))) return; // separator row
    const capability = cells[0].replace(/[*_`]/g, '').trim();
    if (!capability || /^capabilit/i.test(capability)) return; // empty or header row
    let covIdx = -1;
    cells.forEach((c, i) => {
      const v = c.toLowerCase();
      if (v === 'gap' || v === 'partial' || v === 'covered') covIdx = i;
    });
    const methodRe = /\b(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b/i;
    // The API column is the cell carrying an HTTP method (the "Recommended API /
    // Endpoint" column). The newer table inserts a Basepath column before it, so we
    // can't just take the first non-coverage cell; fall back to that only for older
    // tables that have no method-bearing cell.
    let apiIdx = cells.findIndex((c, i) => i >= 1 && i !== covIdx && methodRe.test(c));
    if (apiIdx === -1) apiIdx = cells.findIndex((_, i) => i >= 1 && i !== covIdx);
    const rawApi = apiIdx !== -1 ? cells[apiIdx].replace(/[*_`]/g, '').trim() : '';
    const methodMatch = rawApi.match(methodRe);
    const method = methodMatch ? methodMatch[1].toUpperCase() : '';
    const pathMatch = rawApi.match(/(\/[A-Za-z0-9_\-{}/:.]+)/);
    // Keep only the endpoint path from the "Recommended API / Endpoint" column
    // (e.g. "/testLists"); the Basepath column is intentionally not prepended.
    const endpointPath = pathMatch ? pathMatch[1] : '';
    // Keep only the API name — drop any "(GET /path)" / method / URL suffix.
    const api = rawApi
      .replace(/\s*[([].*$/, '')
      .replace(/\s*\b(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b.*$/i, '')
      .replace(/\s*\/.*$/, '')
      .replace(/[-–—:\s]+$/, '')
      .trim() || rawApi;
    rows.push({ capability, api, method, endpointPath });
  });
  return rows;
};

// True when the markdown looks like an epic coverage analysis (a GFM table that
// contains coverage values), so we know to offer a PDF export for it.
export const isCoverageAnalysis = (md: string): boolean => {
  if (!md || md.indexOf('|') === -1) return false;
  const hasTable = /\|.*\|/.test(md) && /\|\s*:?-{3,}/.test(md);
  const hasCoverage = /\b(gap|partial|covered)\b/i.test(md) || /under\s+review/i.test(md);
  return hasTable && hasCoverage;
};

// Rebuilds a markdown separator row (`| --- | --- |`) matching a header's columns.
const separatorForHeader = (headerRow: string): string => {
  const cols = headerRow.replace(/^\|/, '').replace(/\|$/, '').split('|').length;
  return `|${' --- |'.repeat(Math.max(cols, 1))}`;
};

// Merges several coverage-analysis replies (one per text chunk) into a single
// document: keeps the first reply's intro, then combines each group's table rows
// under one header, de-duplicating rows by capability. Non-analysis replies are
// simply concatenated with dividers.
export const mergeAnalyses = (replies: string[]): string => {
  const valid = replies.filter(r => r && r.trim() && !/^(Sorry, |An error occurred)/i.test(r.trim()));
  if (valid.length <= 1) return valid[0] ?? replies.find(r => r?.trim()) ?? "";
  if (!valid.some(isCoverageAnalysis)) return valid.join('\n\n---\n\n');

  const isTableLine = (l: string) => l.trim().startsWith('|');
  const isSeparator = (l: string) => /^\s*\|?[\s:|-]*-{3,}[\s:|-]*$/.test(l);

  type Group = { heading: string; header: string; separator: string; rows: string[]; seen: Set<string> };
  const order: string[] = [];
  const groups = new Map<string, Group>();

  // Intro = leading lines of the first reply before its first heading or table.
  const introLines: string[] = [];
  for (const l of valid[0].split('\n')) {
    if (/^#{1,6}\s+/.test(l) || isTableLine(l)) break;
    introLines.push(l);
  }

  valid.forEach(reply => {
    const lines = reply.split('\n');
    let currentHeading = "";
    let inTable = false;
    let group: Group | null = null;

    for (const line of lines) {
      const headingMatch = line.match(/^#{1,6}\s+(.+?)\s*$/);
      if (headingMatch) {
        currentHeading = headingMatch[1].trim();
        inTable = false;
        group = null;
        continue;
      }
      if (!isTableLine(line)) { inTable = false; continue; }

      if (!inTable) {
        // First table line under this heading = its header row.
        inTable = true;
        const key = currentHeading || `__ungrouped_${order.length}`;
        if (!groups.has(key)) {
          groups.set(key, { heading: currentHeading, header: line.trim(), separator: '', rows: [], seen: new Set() });
          order.push(key);
        }
        group = groups.get(key)!;
        continue;
      }
      if (isSeparator(line)) { if (group && !group.separator) group.separator = line.trim(); continue; }

      if (group) {
        const firstCell = line.replace(/^\|/, '').split('|')[0].replace(/[*_`]/g, '').trim().toLowerCase();
        if (firstCell && !group.seen.has(firstCell)) {
          group.seen.add(firstCell);
          group.rows.push(line.trim());
        }
      }
    }
  });

  const out: string[] = [];
  const intro = introLines.join('\n').trim();
  if (intro) out.push(intro, '');
  for (const key of order) {
    const g = groups.get(key)!;
    if (!g.rows.length) continue;
    if (g.heading) out.push(`### ${g.heading}`);
    out.push(g.header);
    out.push(g.separator || separatorForHeader(g.header));
    out.push(...g.rows, '');
  }
  return out.join('\n').trim();
};
